import { useEffect, useMemo, useRef, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import { initJanusForFourVehicle } from "../../../utils/fourvehicleVideo";

type Props = {
  baseId: number | null;
  vehicleId: number | null;
  zaoSuid: string | null;
  isVisible: boolean;
  fallbackLogoSrc: string;
};

// bpslog画面でJanus映像を表示し、表示状態やID変更に応じて接続/切断する。
const BpsLogVideo = ({
  baseId,
  vehicleId,
  zaoSuid,
  isVisible,
  fallbackLogoSrc,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const remoteHandleRef = useRef<any | null>(null);
  const [hasStream, setHasStream] = useState(false);

  const normalizedSuid = useMemo(() => {
    const s = (zaoSuid ?? "").trim();
    return s.length ? s : null;
  }, [zaoSuid]);

  useEffect(() => {
    setHasStream(false);

    if (!isVisible) return;
    if (!baseId) return;
    if (vehicleId === null) return;
    if (!normalizedSuid) return;

    const videoEl = videoRef.current;
    if (!videoEl) return;

    let cancelled = false;

    // video要素のsrcObjectを安全にクリアする。
    const cleanupVideoEl = () => {
      try {
        videoEl.srcObject = null;
      } catch {
        // 無視
      }
    };

    // リモートハンドルを安全に切断/破棄する。
    const cleanupRemoteHandle = () => {
      const handle = remoteHandleRef.current;
      remoteHandleRef.current = null;
      if (!handle) return;

      try {
        handle.hangup?.();
      } catch {
        // 無視
      }

      try {
        handle.detach?.();
      } catch {
        // 無視
      }
    };

    // Janus設定取得→接続初期化を行い、受信開始/エラーをUIへ反映する。
    const run = async () => {
      try {
        const janusConfig = await operatorApiService.doGetJanusConfig({
          baseId,
        });
        if (cancelled) return;

        const janusServer = janusConfig?.janusServer;
        const janusSdkServer = janusConfig?.janusSdkServer;
        const credential = janusConfig?.credential;

        if (
          typeof janusServer !== "string" ||
          typeof janusSdkServer !== "string" ||
          typeof credential !== "string"
        ) {
          return;
        }

        await initJanusForFourVehicle({
          janusServer,
          janusSdkServer,
          sdkServerUserInfo: { credential },
          suid: normalizedSuid,
          videoEl,
          onStreamStart: () => {
            if (cancelled) return;
            setHasStream(true);
          },
          onRemoteHandle: (handle) => {
            remoteHandleRef.current = handle;
          },
          onError: () => {
            if (cancelled) return;
            setHasStream(false);
          },
        });
      } catch (err) {
        if (cancelled) return;
        setHasStream(false);
        console.error("bpslog video init error:", err);
      }
    };

    run();

    return () => {
      cancelled = true;
      setHasStream(false);
      cleanupRemoteHandle();
      cleanupVideoEl();
    };
  }, [baseId, vehicleId, normalizedSuid, isVisible]);

  return (
    <div className="bpslogVideo">
      <div className="bpslogVideoInner" aria-label="車両映像">
        <div className="bpslogVideoFallback" aria-hidden={hasStream}>
          <img src={fallbackLogoSrc} alt="" />
        </div>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={hasStream ? { display: "block" } : { display: "none" }}
        />
      </div>
    </div>
  );
};

export default BpsLogVideo;
